import { isTauri } from '@/tauri/utils';
import { type EventCallback, listen } from '@tauri-apps/api/event';
import {
  type ChronicleAudioRmsEvent,
  type ChronicleDeviceSummary,
  type ChronicleStreamErrorEvent,
  type ChronicleStreamFinishedEvent,
  type ChronicleStreamStartedEvent,
  type ChronicleStreamState,
  type ChronicleSyncCompletedEvent,
  type ChronicleSyncFailedEvent,
  type ChronicleSyncProgressEvent,
  type DeviceState,
  type OtaCompletedEvent,
  type OtaFailedEvent,
  type OtaStateEvent,
} from './types';

export const CHRONICLE_EVENTS = {
  STATE_CHANGED: 'chronicle:state_changed',
  DEVICE_DISCOVERED: 'chronicle:device_discovered',
  SYNC_PROGRESS: 'chronicle:sync_progress',
  SYNC_COMPLETED: 'chronicle:sync_completed',
  SYNC_FAILED: 'chronicle:sync_failed',
  AUDIO_RMS: 'chronicle:audio_rms',
  STREAM_STATE: 'chronicle:stream_state',
  STREAM_STARTED: 'chronicle:stream_started',
  STREAM_FINISHED: 'chronicle:stream_finished',
  STREAM_ERROR: 'chronicle:stream_error',
  TOKEN_REFRESH_NEEDED: 'chronicle:token_refresh_needed',
  OTA_STATE: 'chronicle:ota_state',
  OTA_COMPLETED: 'chronicle:ota_completed',
  OTA_FAILED: 'chronicle:ota_failed',
  RECORDING_RECOVERED: 'chronicle:recording_recovered',
  AUTO_SESSION_CREATED: 'chronicle:auto_session_created',
  OBSERVABILITY: 'chronicle:observability',
} as const;

/**
 * Payload of `chronicle:auto_session_created` — the auto-session watcher just
 * created a genuinely-NEW backend session for a device-initiated recording
 * (duplicates / recovered recordings emit `chronicle:recording_recovered`
 * instead). Mirrors `AutoSessionCreatedEvent` in
 * `src-tauri/src/chronicle/auto_session.rs`.
 */
export interface AutoSessionCreatedEvent {
  sessionId: string;
  startedAt: string | null;
  /** Device recording filename — null when the device hasn't reported it yet. */
  fileName: string | null;
}

export interface RecordingRecoveredEvent {
  sessionId: string | null;
  /** Device filename the recovery bound — dropped when it no longer matches
   *  the live recording (stale-recovery race, APP-7879). */
  fileName: string;
  durationSecs: number;
  isPaused: boolean;
  recoveredMatchVia:
    | 'idempotent'
    | 'active'
    | 'pending'
    | 'filename_match'
    | 'created_offline'
    | 'unknown';
  recoveredFormat: 'chunked' | 'legacy' | 'unknown';
  errorCode?: string;
}

export async function listenChronicleSyncProgress(
  cb: EventCallback<ChronicleSyncProgressEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleSyncProgressEvent>(CHRONICLE_EVENTS.SYNC_PROGRESS, cb);
}

export async function listenChronicleStateChanged(cb: EventCallback<DeviceState>) {
  if (!isTauri()) return () => {};
  return listen<DeviceState>(CHRONICLE_EVENTS.STATE_CHANGED, cb);
}

export async function listenChronicleDeviceDiscovered(
  cb: EventCallback<ChronicleDeviceSummary>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleDeviceSummary>(CHRONICLE_EVENTS.DEVICE_DISCOVERED, cb);
}

export async function listenChronicleSyncCompleted(
  cb: EventCallback<ChronicleSyncCompletedEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleSyncCompletedEvent>(CHRONICLE_EVENTS.SYNC_COMPLETED, cb);
}

export async function listenChronicleSyncFailed(
  cb: EventCallback<ChronicleSyncFailedEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleSyncFailedEvent>(CHRONICLE_EVENTS.SYNC_FAILED, cb);
}

export async function listenChronicleAudioRms(cb: EventCallback<ChronicleAudioRmsEvent>) {
  if (!isTauri()) return () => {};
  return listen<ChronicleAudioRmsEvent>(CHRONICLE_EVENTS.AUDIO_RMS, cb);
}

export async function listenChronicleStreamState(
  cb: EventCallback<ChronicleStreamState>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleStreamState>(CHRONICLE_EVENTS.STREAM_STATE, cb);
}

export async function listenChronicleStreamStarted(
  cb: EventCallback<ChronicleStreamStartedEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleStreamStartedEvent>(CHRONICLE_EVENTS.STREAM_STARTED, cb);
}

export async function listenChronicleStreamFinished(
  cb: EventCallback<ChronicleStreamFinishedEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleStreamFinishedEvent>(CHRONICLE_EVENTS.STREAM_FINISHED, cb);
}

export async function listenChronicleStreamError(
  cb: EventCallback<ChronicleStreamErrorEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleStreamErrorEvent>(CHRONICLE_EVENTS.STREAM_ERROR, cb);
}

export async function listenChronicleTokenRefreshNeeded(cb: EventCallback<unknown>) {
  if (!isTauri()) return () => {};
  return listen(CHRONICLE_EVENTS.TOKEN_REFRESH_NEEDED, cb);
}

export async function listenChronicleOtaState(cb: EventCallback<OtaStateEvent>) {
  if (!isTauri()) return () => {};
  return listen<OtaStateEvent>(CHRONICLE_EVENTS.OTA_STATE, cb);
}

export async function listenChronicleOtaCompleted(cb: EventCallback<OtaCompletedEvent>) {
  if (!isTauri()) return () => {};
  return listen<OtaCompletedEvent>(CHRONICLE_EVENTS.OTA_COMPLETED, cb);
}

export async function listenChronicleOtaFailed(cb: EventCallback<OtaFailedEvent>) {
  if (!isTauri()) return () => {};
  return listen<OtaFailedEvent>(CHRONICLE_EVENTS.OTA_FAILED, cb);
}

export async function listenChronicleRecordingRecovered(
  cb: EventCallback<RecordingRecoveredEvent>
) {
  if (!isTauri()) return () => {};
  return listen<RecordingRecoveredEvent>(CHRONICLE_EVENTS.RECORDING_RECOVERED, cb);
}

export async function listenChronicleAutoSessionCreated(
  cb: EventCallback<AutoSessionCreatedEvent>
) {
  if (!isTauri()) return () => {};
  return listen<AutoSessionCreatedEvent>(CHRONICLE_EVENTS.AUTO_SESSION_CREATED, cb);
}

/**
 * Payload of `chronicle:observability` — Datadog-only forensic breadcrumb from
 * the Rust Chronicle live path; the live twin of `bulk_download:observability`.
 */
export interface ChronicleObservabilityEvent {
  eventName: string;
  level: 'info' | 'warn' | 'error';
  attributes: Record<string, string | number | boolean>;
}

export async function listenChronicleObservability(
  cb: EventCallback<ChronicleObservabilityEvent>
) {
  if (!isTauri()) return () => {};
  return listen<ChronicleObservabilityEvent>(CHRONICLE_EVENTS.OBSERVABILITY, cb);
}
